import { DollarSign, TrendingDown, TrendingUp, AlertTriangle } from 'lucide-react';
import React from 'react';
import { useGetBudgetSummaryQuery } from '../../services/dashboardApi';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

const BudgetWidget: React.FC<WidgetProps> = ({ scope, projectId, size }) => {
  const { data, isLoading, error } = useGetBudgetSummaryQuery(
    { projectId: scope === 'project' ? projectId : undefined },
    { pollingInterval: 300000 } // 5 minute polling
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={4} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">Failed to load budget data</p>
          <p className="text-gray-500 text-xs mt-1">Please try refreshing</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <WidgetEmptyState
        title="No Budget Data"
        description="Budget information is not available for this project"
        icon={DollarSign}
      />
    );
  }

  const utilizationPercent =
    data.revisedBudget > 0 ? ((data.actual / data.revisedBudget) * 100).toFixed(1) : '0';

  const isOverBudget = data.variance < 0;
  const isCompact = size === '1x1';

  return (
    <div className="space-y-4">
      {/* Budget Overview */}
      <div className="grid grid-cols-2 gap-3">
        {/* Total Budget */}
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 font-medium mb-1">
            {scope === 'org' ? 'Total Budget' : 'Revised Budget'}
          </p>
          <p className="text-lg font-bold text-gray-900">{formatCurrency(data.revisedBudget)}</p>
        </div>

        {/* Actual Spent */}
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 font-medium mb-1">Actual Spent</p>
          <p className="text-lg font-bold text-gray-900">{formatCurrency(data.actual)}</p>
        </div>
      </div>

      {/* Variance Indicator */}
      <div className={`rounded-lg p-3 ${isOverBudget ? 'bg-red-50' : 'bg-green-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isOverBudget ? (
              <TrendingDown className="h-4 w-4 text-red-600" />
            ) : (
              <TrendingUp className="h-4 w-4 text-green-600" />
            )}
            <span className="text-xs font-medium text-gray-700">Variance</span>
          </div>
          <div className={`text-sm font-bold ${isOverBudget ? 'text-red-600' : 'text-green-600'}`}>
            {formatCurrency(Math.abs(data.variance))} ({formatPercent(data.variancePercent)})
          </div>
        </div>
      </div>

      {/* Utilization Progress */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-gray-600">Budget Utilization</span>
          <span className="text-xs font-bold text-gray-900">{utilizationPercent}%</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              parseFloat(utilizationPercent) > 100
                ? 'bg-red-500'
                : parseFloat(utilizationPercent) > 80
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(parseFloat(utilizationPercent), 100)}%` }}
          />
        </div>
      </div>

      {/* Category Breakdown (only for larger sizes) */}
      {!isCompact && data.byCategory && data.byCategory.length > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">By Category</p>
          <div className="space-y-2">
            {data.byCategory.slice(0, 3).map((cat, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <span className="text-gray-600 truncate max-w-[120px]">{cat.category}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-900 font-medium">{formatCurrency(cat.actual)}</span>
                  <span className={cat.variance < 0 ? 'text-red-600' : 'text-green-600'}>
                    ({cat.variance < 0 ? '-' : '+'}
                    {formatCurrency(Math.abs(cat.variance))})
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Committed & Forecast (only for 2x2) */}
      {size === '2x2' && (
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
          <div>
            <p className="text-xs text-gray-500 font-medium mb-1">Committed</p>
            <p className="text-sm font-bold text-gray-900">{formatCurrency(data.committed)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium mb-1">Forecast</p>
            <p className="text-sm font-bold text-gray-900">{formatCurrency(data.forecast)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetWidget;
